/** PipeKit engineering-core — canonical unit system.
 *  All calculation inputs convert to canonical SI base internally, so displayed
 *  units and internal units can never be confused. Conversion is unit-safe:
 *  unknown units throw at runtime, and dimensions are enforced by the types. */

export type Dimension =
  | "length"        // canonical: m
  | "diameter"      // canonical: m  (internal diameter)
  | "velocity"      // canonical: m/s
  | "volumeFlow"    // canonical: m³/s
  | "pressure"      // canonical: Pa
  | "head"          // canonical: m H2O
  | "power"         // canonical: W
  | "area"          // canonical: m²
  | "rainfall"      // canonical: m/s (rate)
  | "temperature"   // canonical: °C (affine)
  | "deltaTemp";    // canonical: K

export interface UnitDef { readonly factor: number; readonly label: string }
export type UnitMap = Readonly<Record<string, UnitDef>>

const UNITS: Record<Dimension, UnitMap> = {
  length:       { m:{factor:1,label:"m"}, mm:{factor:1e-3,label:"mm"}, cm:{factor:1e-2,label:"cm"}, km:{factor:1e3,label:"km"}, in:{factor:0.0254,label:"in"}, ft:{factor:0.3048,label:"ft"}, yd:{factor:0.9144,label:"yd"} },
  diameter:     { m:{factor:1,label:"m"}, mm:{factor:1e-3,label:"mm"}, in:{factor:0.0254,label:"in"}, cm:{factor:1e-2,label:"cm"} },
  velocity:     { "m/s":{factor:1,label:"m/s"}, "ft/s":{factor:0.3048,label:"ft/s"}, "km/h":{factor:1/3.6,label:"km/h"} },
  volumeFlow:   { "m³/s":{factor:1,label:"m³/s"}, "m³/h":{factor:1/3600,label:"m³/h"}, "L/s":{factor:1e-3,label:"L/s"}, "L/min":{factor:1e-3/60,label:"L/min"}, GPM:{factor:6.30901964e-5,label:"US gal/min"}, "ft³/s":{factor:0.02831686,label:"ft³/s"}, "ft³/min":{factor:0.02831686/60,label:"ft³/min"} },
  pressure:     { Pa:{factor:1,label:"Pa"}, kPa:{factor:1e3,label:"kPa"}, MPa:{factor:1e6,label:"MPa"}, bar:{factor:1e5,label:"bar"}, psi:{factor:6894.757,label:"psi"} },
  head:         { "m":{factor:1,label:"m H₂O"}, "ft":{factor:0.3048,label:"ft H₂O"} },
  power:        { W:{factor:1,label:"W"}, kW:{factor:1e3,label:"kW"}, BTU_hr:{factor:0.29307107,label:"BTU/hr"}, "kBTU/hr":{factor:293.07107,label:"kBTU/hr"} },
  area:         { "m²":{factor:1,label:"m²"}, "ft²":{factor:0.09290304,label:"ft²"} },
  rainfall:     { "m/s":{factor:1,label:"m/s"}, "mm/hr":{factor:1/3.6e6,label:"mm/hr"}, "in/hr":{factor:0.0254/3600,label:"in/hr"} },
  temperature:  { "°C":{factor:1,label:"°C"}, K:{factor:1,label:"K"} },   // affine — see temp funcs
  deltaTemp:    { "K":{factor:1,label:"K"} },
};

export function isUnit(dim: Dimension, unit: string): boolean {
  return Object.prototype.hasOwnProperty.call(UNITS[dim], unit);
}
export function unitsOf(dim: Dimension): string[] { return Object.keys(UNITS[dim]); }
export function unitLabel(dim: Dimension, unit: string): string {
  return UNITS[dim][unit]?.label ?? unit;
}
/** Linear conversion to canonical SI. Throws on unknown unit. */
export function toCanonical(d: Dimension, unit: string, value: number): number {
  if (d === "temperature") throw new Error("temperature uses affine conversion");
  const u = UNITS[d][unit];
  if (!u) throw new Error(`Unknown unit '${unit}' for dimension '${d}'`);
  return value * u.factor;
}
export function fromCanonical(d: Dimension, unit: string, canon: number): number {
  if (d === "temperature") throw new Error("temperature uses affine conversion");
  const u = UNITS[d][unit];
  if (!u) throw new Error(`Unknown unit '${unit}' for dimension '${d}'`);
  return canon / u.factor;
}
export function scaleCanonical(d: Dimension, unit: string): number {
  if (d === "temperature") return 1;
  const u = UNITS[d][unit];
  if (!u) throw new Error(`Unknown unit '${unit}' for dimension '${d}'`);
  return u.factor;
}
/** Convert a value directly between two units of the same dimension (canonical-safe). */
export function convertDirect(d: Dimension, from: string, to: string, value: number): number {
  if (d === "temperature") {
    const c = from === "°C" ? value : value - 273.15;
    return to === "°C" ? c : c + 273.15;
  }
  return value * scaleCanonical(d, from) / scaleCanonical(d, to);
}
/** Round-trip identity helper for tests. */
export function roundTripConvert(d: Dimension, unit: string): (v: number) => number {
  return (v) => convertDirect(d, unit, unit, v);
}
/** Format a value expressed in `unit` for `dim` with fixed decimals. */
export function fmtUnit(d: Dimension, unit: string, value: number, decimals = 2): string {
  return value.toLocaleString(undefined, { maximumFractionDigits: decimals }) + " " + unitLabel(d, unit);
}